import { useState, useEffect, useCallback, useRef } from 'react';
import { ensureFIGVault } from '../../api/figApi';
import './FIG.css';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const SIMPLE_PUT_LIMIT = 4 * 1024 * 1024; // 4 MB — Graph's limit for single-shot PUT

// ---- Formatters ----
const fmtSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

// ---- File icons (same palette as family vault) ----
const EXT_TYPE = {
  img: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg'],
  pdf: ['pdf'],
  doc: ['doc', 'docx'],
  xls: ['xls', 'xlsx'],
  ppt: ['ppt', 'pptx'],
  zip: ['zip', '7z', 'rar', 'tar', 'gz'],
};
function fileType(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  for (const [k, v] of Object.entries(EXT_TYPE)) if (v.includes(ext)) return k;
  return 'file';
}
const ICON_LABELS = { img: 'IMG', pdf: 'PDF', doc: 'DOC', xls: 'XLS', ppt: 'PPT', zip: 'ZIP', file: 'FILE' };
const ICON_COLORS = { img: '#2e7d32', pdf: '#c62828', doc: '#1565c0', xls: '#2e7d32', ppt: '#d84315', zip: '#6a1b9a', file: '#616161' };

const sanitizeFolderName = (s) =>
  String(s || '').replace(/[\\/:*?"<>|]/g, '').trim();

const FolderGlyph = ({ fill = '#f5c518' }) => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill={fill} stroke="#4B0082"
    strokeWidth="1.2" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

const FileGlyph = ({ name }) => {
  const type = fileType(name);
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 4, background: ICON_COLORS[type],
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.5px', flexShrink: 0,
    }}>
      {ICON_LABELS[type]}
    </div>
  );
};

// ---- Upload helpers ----

async function simpleUpload(token, driveId, parentIdOrNull, fileName, file) {
  const url = parentIdOrNull
    ? `${GRAPH}/drives/${driveId}/items/${parentIdOrNull}:/${encodeURIComponent(fileName)}:/content`
    : `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(fileName)}:/content`;
  const body = await file.arrayBuffer();
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    body,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status}): ${await res.text()}`);
  return res.json();
}

async function chunkedUpload(token, driveId, parentIdOrNull, fileName, file, onProgress) {
  const sessUrl = parentIdOrNull
    ? `${GRAPH}/drives/${driveId}/items/${parentIdOrNull}:/${encodeURIComponent(fileName)}:/createUploadSession`
    : `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(fileName)}:/createUploadSession`;
  const sessRes = await fetch(sessUrl, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': 'replace' } }),
  });
  if (!sessRes.ok) throw new Error(`Upload session failed (${sessRes.status}): ${await sessRes.text()}`);
  const sess = await sessRes.json();
  const uploadUrl = sess.uploadUrl;

  const CHUNK = 5 * 1024 * 1024; // 5 MB — must be a multiple of 320 KiB; 5 MB works
  const total = file.size;
  let start = 0;
  let last = null;
  while (start < total) {
    const end = Math.min(start + CHUNK, total);
    const chunk = file.slice(start, end);
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Length': String(end - start),
        'Content-Range': `bytes ${start}-${end - 1}/${total}`,
      },
      body: chunk,
    });
    if (!res.ok && res.status !== 202) {
      throw new Error(`Chunk upload failed (${res.status}): ${await res.text()}`);
    }
    if (res.status === 200 || res.status === 201) {
      last = await res.json();
    }
    start = end;
    if (onProgress) onProgress(start, total);
  }
  return last;
}

async function uploadFile(token, driveId, parentIdOrNull, fileName, file, onProgress) {
  if (file.size <= SIMPLE_PUT_LIMIT) {
    const r = await simpleUpload(token, driveId, parentIdOrNull, fileName, file);
    if (onProgress) onProgress(file.size, file.size);
    return r;
  }
  return chunkedUpload(token, driveId, parentIdOrNull, fileName, file, onProgress);
}

// ---- Component ----

export default function FIGVault({ getToken, initialPath }) {
  const [driveId, setDriveId] = useState(null);
  const [pathStack, setPathStack] = useState([]); // [{ id, name }]
  const [initialPathResolved, setInitialPathResolved] = useState(!initialPath);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creating, setCreating] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const fileInputRef = useRef(null);

  const currentFolderId = pathStack.length > 0 ? pathStack[pathStack.length - 1].id : null;

  const fetchItems = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const did = driveId || (await ensureFIGVault(token));
      if (!driveId) setDriveId(did);
      const url = currentFolderId
        ? `${GRAPH}/drives/${did}/items/${currentFolderId}/children`
          + `?$select=name,id,folder,file,size,lastModifiedDateTime,webUrl&$top=500`
        : `${GRAPH}/drives/${did}/root/children`
          + `?$select=name,id,folder,file,size,lastModifiedDateTime,webUrl&$top=500`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Load failed (${res.status}): ${await res.text()}`);
      const data = await res.json();
      const sorted = (data.value || [])
        .filter(it => !(it.folder && (it.name || '').startsWith('_')))
        .sort((a, b) => {
          // Folders first, then alphabetical — but "Folios" always last among folders
          if (!!a.folder !== !!b.folder) return a.folder ? -1 : 1;
          if (a.folder && b.folder) {
            const aFolios = (a.name || '').toLowerCase() === 'folios' ? 1 : 0;
            const bFolios = (b.name || '').toLowerCase() === 'folios' ? 1 : 0;
            if (aFolios !== bFolios) return aFolios - bFolios;
          }
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });
      setItems(sorted);
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, driveId, currentFolderId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // --- Resolve initialPath on mount ---
  useEffect(() => {
    if (!initialPath || initialPathResolved) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const did = driveId || (await ensureFIGVault(token));
        if (!driveId) setDriveId(did);
        const segments = initialPath.split('/').map(s => s.trim()).filter(Boolean);
        const stack = [];
        let parentId = null;
        for (const seg of segments) {
          const url = parentId
            ? `${GRAPH}/drives/${did}/items/${parentId}/children?$select=name,id,folder&$top=200`
            : `${GRAPH}/drives/${did}/root/children?$select=name,id,folder&$top=200`;
          const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) break;
          const data = await res.json();
          const match = (data.value || []).find(
            f => f.folder && f.name.toLowerCase() === seg.toLowerCase()
          );
          if (!match) break;
          stack.push({ id: match.id, name: match.name });
          parentId = match.id;
        }
        if (!cancelled && stack.length > 0) {
          setPathStack(stack);
        }
      } catch (e) {
        console.warn('[FIGVault] Failed to resolve initialPath:', e.message);
      } finally {
        if (!cancelled) setInitialPathResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [initialPath, initialPathResolved, getToken, driveId]);

  // --- Navigation ---
  function enterFolder(item) {
    setPathStack(prev => [...prev, { id: item.id, name: item.name }]);
    setSearch('');
  }
  function goToBreadcrumb(idx) {
    // idx === -1 → root
    setPathStack(prev => (idx < 0 ? [] : prev.slice(0, idx + 1)));
    setSearch('');
  }

  // --- Create folder ---
  async function handleCreateFolder() {
    const name = sanitizeFolderName(newFolderName);
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const token = await getToken();
      const url = currentFolderId
        ? `${GRAPH}/drives/${driveId}/items/${currentFolderId}/children`
        : `${GRAPH}/drives/${driveId}/root/children`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, folder: {}, '@microsoft.graph.conflictBehavior': 'rename',
        }),
      });
      if (!res.ok) throw new Error(`Create folder failed (${res.status}): ${await res.text()}`);
      setNewFolderOpen(false);
      setNewFolderName('');
      await fetchItems();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  }

  // --- Upload ---
  function triggerUpload() {
    fileInputRef.current?.click();
  }
  async function handleFileSelected(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const token = await getToken();
      let i = 0;
      for (const file of files) {
        i++;
        setUploadProgress({ current: 0, total: file.size,
          name: `${file.name} (${i}/${files.length})` });
        await uploadFile(token, driveId, currentFolderId, file.name, file,
          (current, total) => setUploadProgress({ current, total,
            name: `${file.name} (${i}/${files.length})` })
        );
      }
      await fetchItems();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }

  // --- Download ---
  async function handleDownload(item) {
    try {
      const token = await getToken();
      const res = await fetch(`${GRAPH}/drives/${driveId}/items/${item.id}/content`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  }

  // --- Rename (in-place) ---
  function startRename(item) {
    setRenamingId(item.id);
    setRenameValue(item.name);
  }
  async function commitRename(item) {
    const name = String(renameValue || '').trim();
    if (!name || name === item.name) {
      setRenamingId(null);
      return;
    }
    try {
      const token = await getToken();
      const res = await fetch(`${GRAPH}/drives/${driveId}/items/${item.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(`Rename failed (${res.status}): ${await res.text()}`);
      setRenamingId(null);
      await fetchItems();
    } catch (e) {
      setError(e.message);
      setRenamingId(null);
    }
  }

  // --- Delete ---
  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${GRAPH}/drives/${driveId}/items/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 204) {
        throw new Error(`Delete failed (${res.status}): ${await res.text()}`);
      }
      await fetchItems();
    } catch (e) {
      setError(e.message);
    }
  }

  // --- Filtering ---
  const q = search.trim().toLowerCase();
  const filteredItems = q
    ? items.filter(it => (it.name || '').toLowerCase().includes(q))
    : items;
  const folders = filteredItems.filter(it => it.folder);
  const files = filteredItems.filter(it => it.file);

  // LOB summary — shown when viewing a client's LOB folder list (depth ≥ 2, e.g. Clients/{Company}/)
  const lobSummary = (() => {
    if (pathStack.length < 2 || folders.length === 0) return null;
    const lobFolders = folders.filter(f => (f.name || '').toLowerCase() !== 'folios');
    if (lobFolders.length === 0) return null;
    const lobCount = lobFolders.length;
    const policyCount = lobFolders.reduce((sum, f) => sum + (f.folder?.childCount || 0), 0);
    return { lobCount, policyCount };
  })();

  // ---- Render helpers ----
  const IconBtn = ({ onClick, title, children, color = '#4B0082' }) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={title}
      style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px',
        cursor: 'pointer', padding: '3px 7px', fontSize: '0.8rem', color, lineHeight: 1 }}>
      {children}
    </button>
  );

  return (
    <div>
      {/* Breadcrumbs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        padding: '0.4rem 0 0.6rem', fontSize: '0.88rem', fontWeight: 600 }}>
        <span onClick={() => goToBreadcrumb(-1)}
          style={{ cursor: 'pointer', color: pathStack.length ? '#4B0082' : '#333' }}>
          FIGVault
        </span>
        {pathStack.map((seg, i) => (
          <span key={seg.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#888' }}>/</span>
            <span onClick={() => goToBreadcrumb(i)}
              style={{
                cursor: i === pathStack.length - 1 ? 'default' : 'pointer',
                color: i === pathStack.length - 1 ? '#333' : '#4B0082',
              }}>
              {seg.name}
            </span>
          </span>
        ))}
      </div>

      {/* LOB summary */}
      {lobSummary && (
        <div style={{ fontSize: '0.78rem', color: '#5B2E91', fontWeight: 500,
          padding: '0 0 0.5rem', letterSpacing: '0.2px' }}>
          {lobSummary.lobCount} Line{lobSummary.lobCount === 1 ? '' : 's'} of Business
          {' · '}
          {lobSummary.policyCount} Active Polic{lobSummary.policyCount === 1 ? 'y' : 'ies'}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="FIG-search"
          type="text"
          placeholder={`Search ${pathStack.length ? pathStack[pathStack.length - 1].name : 'FIGVault'}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 220px' }}
        />
        <button className="FIG-add-btn" onClick={triggerUpload} disabled={uploading || !driveId}>
          {uploading ? 'Uploading...' : '+ Upload'}
        </button>
        <button className="FIG-add-btn" onClick={() => setNewFolderOpen(v => !v)} disabled={!driveId}>
          {newFolderOpen ? 'Cancel' : '+ New Folder'}
        </button>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
          onChange={handleFileSelected} />
      </div>

      {/* Inline new folder */}
      {newFolderOpen && (
        <div style={{ display: 'flex', gap: '8px', margin: '0.6rem 0' }}>
          <input
            className="FIG-input"
            type="text"
            placeholder="Folder name"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }}
            autoFocus
            style={{ flex: 1 }}
          />
          <button className="FIG-save-btn" onClick={handleCreateFolder}
            disabled={creating || !newFolderName.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      )}

      {/* Upload progress */}
      {uploading && uploadProgress && (
        <div style={{ margin: '0.6rem 0', padding: '0.5rem 0.6rem', background: '#faf7ee',
          border: '1px solid #d8d1bf', borderRadius: '4px' }}>
          <div style={{ fontSize: '0.78rem', color: '#4B0082', marginBottom: '4px' }}>
            {uploadProgress.name} — {fmtSize(uploadProgress.current)} / {fmtSize(uploadProgress.total)}
          </div>
          <div style={{ height: '6px', background: '#eee7d4', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{
              width: `${uploadProgress.total ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`,
              height: '100%', background: '#4B0082', transition: 'width 0.2s',
            }} />
          </div>
        </div>
      )}

      {error && <p className="FIG-error">{error}</p>}
      {loading && <p className="FIG-loading">Loading vault...</p>}

      {!loading && filteredItems.length === 0 && !error && (
        <p className="FIG-empty">
          {q ? `No matches for "${search}".` : 'This folder is empty.'}
        </p>
      )}

      {/* Folder list */}
      {folders.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '0.4rem' }}>
            Folders ({folders.length})
          </div>
          <div className="FIG-vault-folder-list">
            {folders.map(it => {
              const count = it.folder?.childCount;
              return (
                <div key={it.id}
                  onClick={() => { if (renamingId !== it.id) enterFolder(it); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '0.45rem 0.4rem', borderBottom: '1px solid #f0ebe0',
                    cursor: 'pointer', userSelect: 'none',
                    transition: 'background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f4eedb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <FolderGlyph fill={(it.name || '').toLowerCase() === 'folios' ? '#5B2E91' : undefined} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renamingId === it.id ? (
                      <input
                        className="FIG-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onBlur={() => commitRename(it)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitRename(it);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <span style={{ fontWeight: 600, color: '#333', fontSize: '0.88rem' }}>
                        {it.name}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0, minWidth: '2rem', textAlign: 'right' }}>
                    {count != null ? count : ''}
                  </span>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <IconBtn onClick={() => startRename(it)} title="Rename">&#9998;</IconBtn>
                    <IconBtn onClick={() => handleDelete(it)} title="Delete" color="#8B0000">&#128465;</IconBtn>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div style={{ marginTop: '0.9rem' }}>
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '0.4rem' }}>
            Files ({files.length})
          </div>
          <div>
            {files.map(it => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '10px',
                padding: '0.5rem 0.6rem', borderBottom: '1px solid #eee7d4',
                transition: 'background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f4eedb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                <FileGlyph name={it.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renamingId === it.id ? (
                    <input
                      className="FIG-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => commitRename(it)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename(it);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      autoFocus
                      style={{ width: '100%' }}
                    />
                  ) : (
                    <div style={{ fontWeight: 500, color: '#333', fontSize: '0.85rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.name}
                    </div>
                  )}
                  <div style={{ fontSize: '0.68rem', color: '#888' }}>
                    {fmtSize(it.size)} · {fmtDate(it.lastModifiedDateTime)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {it.webUrl && (
                    <IconBtn onClick={() => window.open(it.webUrl, '_blank')} title="Open in SharePoint">
                      &#128065;
                    </IconBtn>
                  )}
                  <IconBtn onClick={() => handleDownload(it)} title="Download">&#8615;</IconBtn>
                  <IconBtn onClick={() => startRename(it)} title="Rename">&#9998;</IconBtn>
                  <IconBtn onClick={() => handleDelete(it)} title="Delete" color="#8B0000">&#128465;</IconBtn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
